/**
 * Turning a drawn thing into a voice.
 *
 * The paper is a score. WHERE a line sits decides its note — high on the page
 * is a high note, low is low, which is how written music has looked for a
 * thousand years and what a child expects before anyone tells them. So drawing
 * a shape on the page composes a tune, and the sweeping line plays it.
 *
 * The rest of the mark decides the rest of the voice: a long line is a long
 * note, a thick one is a loud one, and each brush is a different material.
 * Every note lands on the same pentatonic scale, so nothing a child draws can
 * come out sounding wrong.
 */

import type { BrushId, Stroke } from '../engine/stroke.ts';

export const MIN_LENGTH = 26;
export const MAX_LENGTH = 1400;

/** How long the drawn line actually is, following every wiggle of it. */
export function strokeLength(stroke: Stroke): number {
  let total = 0;
  for (let i = 1; i < stroke.points.length; i++) {
    total += Math.hypot(stroke.points[i].x - stroke.points[i - 1].x, stroke.points[i].y - stroke.points[i - 1].y);
  }
  return total;
}

/**
 * How many scale steps the height of the paper is divided into. Fifteen is
 * three octaves on a five-note scale — enough range to write a tune in, few
 * enough that each lane is a comfortable target for a finger.
 */
export const LANES = 15;

/**
 * The note a height on the paper sings.
 *
 * This is what makes the room a score rather than a set of noises: WHERE you
 * draw is the tune. High on the page is a high note, low is low, exactly as
 * written music has looked for a thousand years, and a child who has never
 * seen a stave already expects it.
 */
export function degreeForHeight(y: number, height: number): number {
  if (height <= 0) return 0;
  const fraction = Math.max(0, Math.min(1, y / height));
  // Top of the paper is the highest note, so the scale runs backwards from it.
  return Math.round((1 - fraction) * (LANES - 1)) - Math.floor(LANES / 3);
}

/** The y a given lane sits at, for drawing the guide lines. */
export function heightForLane(lane: number, height: number): number {
  return (1 - lane / (LANES - 1)) * height;
}

/**
 * Where a drawn line is at a given across-position, or null if it does not
 * reach that far. This is the point the sweeping line meets it.
 */
export function strokeHeightAt(stroke: Stroke, x: number): number | null {
  const points = stroke.points;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const lo = Math.min(a.x, b.x);
    const hi = Math.max(a.x, b.x);
    if (x < lo || x > hi) continue;
    const span = b.x - a.x;
    if (Math.abs(span) < 1e-6) return a.y;
    return a.y + ((x - a.x) / span) * (b.y - a.y);
  }
  return null;
}

/**
 * How long a line rings. Pitch now comes from height, so length means
 * duration instead — a long line is a long note, which is what a long mark on
 * a page has always meant.
 */
export function durationForLength(length: number): number {
  const clamped = Math.max(MIN_LENGTH, Math.min(MAX_LENGTH, length));
  return 0.18 + (clamped / MAX_LENGTH) * 2.2;
}

export interface Voice {
  degree: number;
  type: OscillatorType;
  gain: number;
  duration: number;
}

/** Each brush is a different material, so each one rings differently. */
const TIMBRE: Record<BrushId, { type: OscillatorType; ring: number }> = {
  pencil: { type: 'sine', ring: 0.7 },
  crayon: { type: 'triangle', ring: 1 },
  marker: { type: 'square', ring: 0.85 },
  rainbow: { type: 'sawtooth', ring: 1.1 },
};

/**
 * The whole voice of one drawn line. `force` is how hard it was struck, 0..1,
 * so the same line played gently and played hard is the same note at two
 * different volumes — which is what makes it feel like an instrument rather
 * than a set of buttons.
 */
export function voiceFor(stroke: Stroke, force: number, atY: number, stageHeight: number): Voice {
  const timbre = TIMBRE[stroke.brush] ?? TIMBRE.crayon;
  // A thicker line has more to it: it rings louder and for longer.
  const heft = Math.max(0.35, Math.min(1.6, stroke.width / 14));
  const hit = Math.max(0.05, Math.min(1, force));

  return {
    degree: degreeForHeight(atY, stageHeight),
    type: timbre.type,
    gain: Math.min(0.34, (0.07 + hit * 0.2) * heft),
    duration: Math.min(3, durationForLength(strokeLength(stroke)) * timbre.ring),
  };
}

/**
 * A rough name for how the line will sound, so the readout can say something
 * true without teaching notation. The bands cover the range the page actually
 * spans — the lowest lane to the highest — rather than an abstract scale.
 */
export function voiceName(degree: number): string {
  if (degree <= -3) return 'very low';
  if (degree <= 0) return 'low';
  if (degree <= 4) return 'middle';
  if (degree <= 7) return 'high';
  return 'very high';
}
